import DataLayerService from "../DataLayerService.js";
import { collectComponentAttributes } from "../auto-tracking/common/component-attributes.js";
import { CUSTOM_ELEMENT_IGNORED_ATTRIBUTES, ECOMMERCE_EVENTS } from "../config.js";

// Events, bei denen item_list_id/item_list_name laut GA4-Spec auf Event-Ebene gehören.
// Für alle anderen Ecommerce-Events (z.B. add_to_cart) wird die Listen-Herkunft pro Item
// aus der productAttribution (localStorage) gezogen — das hier abgeleitete component-Attribut
// (z.B. "hinzugefuegt-overlay") würde sonst die echte Attribution überschreiben.
const EVENT_LEVEL_LIST_PARAMS_EVENTS = ["select_item", "view_item_list"];

export default class DlEvent extends HTMLElement {
    static get tag() {
        return "dl-event";
    }

    static {
        if (!customElements.get(DlEvent.tag)) {
            customElements.define(DlEvent.tag, DlEvent);
        }
    }

    get itemElements() {
        return this.querySelectorAll("dl-product") || this.querySelectorAll("[product-id]") || [];
    }

    get params() {
        return this._getAttributesAsObject(this, true);
    }

    get name() {
        return this.getAttribute('name') || null;
    }

    get items() {
        const items = [];

        for (const element of this.itemElements) {
            const itemData = this._getAttributesAsObject(element);
            if (Object.keys(itemData).length > 0) {
                items.push(itemData);
            }
        }

        return items;
    }

    constructor() {
        super();
        this.datalayerService = new DataLayerService();
    }

    connectedCallback() {
        if (!this.name) {
            console.info("No event name found, can not push to dataLayer. Please make sure the [name] attribute is set.");
            return;
        }
        try {
            const componentAttributes = collectComponentAttributes(this);
            const mergedParams = {
                ...this.params,
                ...componentAttributes,
                ...this._getListContextParams()
            };

            this.datalayerService.pushEvent(this.name, mergedParams, this.items);
        } catch (error) { }
    }

    _getListContextParams(componentElement = this.closest("[component]")) {
        if (!ECOMMERCE_EVENTS.includes(this.name)) {
            return {};
        }

        const params = {};
        const supportsEventLevelListParams = EVENT_LEVEL_LIST_PARAMS_EVENTS.includes(this.name);

        if (componentElement && supportsEventLevelListParams) {
            if (componentElement.getAttribute("component") && !this.params["item-list-id"]) {
                params["item-list-id"] = componentElement.getAttribute("component");
            }
            if (componentElement.getAttribute("product-list") && !this.params["item-list-name"]) {
                params["item-list-name"] = componentElement.getAttribute("product-list");
            }
        }

        const impressionElement = this.closest("[impression]");
        if (impressionElement) {
            params.impression = impressionElement.getAttribute("impression");
        }

        return params;
    }

    /**
     * Extrahiert alle Attribute eines Elements als Key-Value-Objekt.
     * Wird mit filterCustomElementAttributes=true nur für das dl-event Element selbst
     * aufgerufen, damit dort das "name"-Attribut (Event-Name) nicht als Param landet.
     * Für dl-product Children MUSS filtering aus bleiben, sonst geht der Produkt-Name verloren.
     * @private
     */
    _getAttributesAsObject(element, filterCustomElementAttributes = false) {
        const attributes = {};

        if (!element || !element.attributes) {
            return attributes;
        }

        for (const attr of element.attributes) {
            if (filterCustomElementAttributes && CUSTOM_ELEMENT_IGNORED_ATTRIBUTES.includes(attr.name)) {
                continue;
            }
            attributes[attr.name] = attr.value;
        }

        return attributes;
    }
}